import React from 'react';
import { Table, Empty, Tag, Button, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { EMPTY_TEXT, COLUMN_HELP } from './summary';
import DateWithWeekday from '../../components/DateWithWeekday';
import { withKeyboardSort } from '../../components/keyboardSort';
import { RISK_TEXT_COLOR, DOWN_COLOR, MUTED_COLOR, FAINT_COLOR } from '../../components/metricColors';

const pct = (v) => (v == null ? '—' : `${v.toFixed(1)}%`);
const money = (v) => (v == null ? '—' : `NT$${Math.round(v).toLocaleString()}`);

/**
 * 帶問號說明的欄位標題。圖示在可排序的表頭裡，所以攔下 click 與 Enter/空白，
 * 否則想看說明的人會順手把排序翻掉 (keyboardSort 在表頭掛了 Enter 觸發 click)。
 */
const withHelp = (label, help) => (
  <span style={{ whiteSpace: 'nowrap' }}>
    {label}
    <Tooltip title={help} trigger={['hover', 'focus']}>
      <QuestionCircleOutlined aria-label={`${label}的算法`} tabIndex={0}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        style={{ marginLeft: 4, color: FAINT_COLOR, cursor: 'help' }} />
    </Tooltip>
  </span>
);

/**
 * 數字欄的排序。沒有資料 (null，例如清單頭尾沒有鄰日) 不論方向都排最後：
 * antd 降冪時會把比較結果反過來，所以 null 的比較值要跟著方向先反一次。
 */
const num = (get) => (a, b, order) => {
  const x = get(a);
  const y = get(b);
  if (x == null || y == null) {
    if (x == null && y == null) return 0;
    const last = x == null ? 1 : -1;
    return order === 'descend' ? -last : last;
  }
  return x - y;
};

const netPickupCell = (v) => <span style={{ color: v <= 0 ? DOWN_COLOR : undefined }}>{v}</span>;

/** 調漲的建議處方：團體撐盤優先提醒，鄰日偏弱再附最短住宿提示。 */
const raiseRx = (_, r) => (
  <span style={{ whiteSpace: 'nowrap' }}>
    {r.groupRisk ? (
      <>
        <Tag color="orange" style={{ marginInlineEnd: 6 }}>團體撐盤</Tag>
        <span style={{ color: MUTED_COLOR }}>先確認團體訂金與名單</span>
      </>
    ) : <span style={{ color: MUTED_COLOR }}>可調漲</span>}
    {r.weakSides.length ? (
      <span style={{ color: MUTED_COLOR }}>{` / 可考慮 ${r.nights} 晚起訂 (${r.weakSides.join('')})`}</span>
    ) : null}
  </span>
);

export function columnsFor(kind, target, onPickDate) {
  const base = [
    // 日期一律走共用的 DateWithWeekday：它的假日判準來自 holidays 表而不是星期幾。
    // 這對定價表格特別重要 —— 六日可能是春節、補班的週六實際要上班，
    // 只看星期幾會把最該賣高價與最該賣平日價的兩種日子都判反
    // 明細只剩這一個入口，只綁滑鼠等於對鍵盤使用者關門 —— 用真的 <button> 而不是帶 onClick 的 span
    // (PricingActionsCard 的摘要列早就這麼做了，抽屜裡的表格漏掉)
    // 270 而非 200：這一欄的內容是「日期 + 假日標籤」，標籤長度由 holidays 表決定。
    // 實測「孔子誕辰紀念日/教師節」那列會把欄位撐到 267px (270 剛好容得下) —— auto layout 下 width 是
    // 「至少這麼寬」，撐開的部分會從其他欄扣，把「近30日淨進單」壓到 100px 而斷行。
    // 低估這一欄等於讓整張表的寬度預算失準。
    { title: '日期', dataIndex: 'dat', key: 'dat', width: 270,
      render: (d) => (onPickDate ? (
        <Button type="link" size="small" style={{ padding: 0, height: 'auto' }}
          onClick={() => onPickDate(d)} aria-label={`看 ${d} 的訂房曲線`}>
          <DateWithWeekday date={d} />
        </Button>
      ) : <DateWithWeekday date={d} />) },
    { title: '在手率', dataIndex: 'occNow', key: 'occ', align: 'right', width: 80,
      sorter: num((r) => r.occNow), render: pct },
    // 依各表的判準排序：調漲看悲觀預測 (lo)，調降看樂觀預測 (hi)；相同時比另一端
    { title: '預測最終', key: 'band', align: 'right', width: 90,
      sorter: kind === 'raise'
        ? (a, b) => (a.band.lo - b.band.lo) || (a.band.hi - b.band.hi)
        : (a, b) => (a.band.hi - b.band.hi) || (a.band.lo - b.band.lo),
      render: (_, r) => `${r.band.lo.toFixed(0)}–${r.band.hi.toFixed(0)}%` },
  ];
  if (kind === 'raise') {
    // 欄位順序與調降表一一對應 (見測試)，切換抽屜時同一欄不會左右跳動
    return base.concat([
      // 超出調漲上界的百分點 = 漲價後可以讓掉、仍能維持在上界以上的緩衝 (見 rules.js 的 RAISE_CEILING)
      { title: '超出上界', dataIndex: 'headroom', key: 'over', align: 'right', width: 90,
        sorter: (a, b) => a.headroom - b.headroom,
        render: (v) => `+${v.toFixed(1)}pt` },
      { title: '待售間夜', dataIndex: 'unsold', key: 'unsold', align: 'right', width: 90, sorter: num((r) => r.unsold) },
      // 鄰日用中位預測而非在手率：遠期在手每天看起來都很弱，判斷不了能不能包最短住宿
      { title: '前一晚', dataIndex: 'prevMid', key: 'prev', align: 'right', width: 90, render: pct,
        sorter: num((r) => r.prevMid) },
      { title: '後一晚', dataIndex: 'nextMid', key: 'next', align: 'right', width: 90, render: pct,
        sorter: num((r) => r.nextMid) },
      { title: '在手團體', dataIndex: 'otbGroup', key: 'grp', align: 'right', width: 100,
        sorter: num((r) => r.otbGroup),
        render: (v, r) => (v == null ? '—' : (
          <span style={{ color: r.groupRisk ? RISK_TEXT_COLOR : undefined, fontWeight: r.groupRisk ? 600 : undefined }}>{v}</span>
        )) },
      { title: '在手散客', dataIndex: 'otbFit', key: 'fit', align: 'right', width: 100,
        sorter: num((r) => r.otbFit),
        render: (v) => (v == null ? '—' : v) },
      // 390：最長組合「團體撐盤 + 先確認團體訂金與名單 + / 可考慮 N 晚起訂 (前後)」，1920 視窗實測內容最寬 362px，
      // 加儲存格左右內距 16px 為 378px，留 12px 餘裕；總寬必須 <= 1872 (1920 - 抽屜內距 48) 才不會水平捲動
      { title: '建議處方', key: 'rx', width: 390, render: raiseRx },
      { title: '目前 ADR', dataIndex: 'adr', key: 'adr', align: 'right', width: 100, sorter: num((r) => r.adr), render: money },
      // 預設依潛在房租由高到低：先處理滿房後擋掉最多錢的日子；相同時比超出上界
      { title: withHelp('潛在房租', COLUMN_HELP.raiseOverflow), dataIndex: 'overflow', key: 'overflow',
        align: 'right', width: 120, showSorterTooltip: false,   // 否則與說明圖示疊成兩個 tooltip
        sorter: (a, b) => (a.overflow - b.overflow) || (a.headroom - b.headroom), defaultSortOrder: 'descend',
        render: money },
      { title: '近30日淨進單', dataIndex: 'netPickup30', key: 'net', align: 'right', width: 130,
        sorter: num((r) => r.netPickup30), render: netPickupCell },
      { title: withHelp('漲10%可掉到', COLUMN_HELP.raiseFloor10), dataIndex: 'raiseFloor10', key: 'floor',
        align: 'right', width: 130, showSorterTooltip: false, sorter: num((r) => r.raiseFloor10),
        render: (v) => `${v.toFixed(1)}%` },
    ]);
  }
  if (kind === 'lower') {
    return base.concat([
      // 這一欄顯示成負號 (−17.1pt)，所以「由低至高」= 缺口最大的排前面。
      // 排序比的是畫面上那個帶負號的值，與欄位讀起來的方向一致
      { title: '距目標', dataIndex: 'gap', key: 'gap', align: 'right', width: 90,
        sorter: (a, b) => (-a.gap) - (-b.gap), defaultSortOrder: 'ascend',
        render: (v) => `−${v.toFixed(1)}pt` },
      { title: '待售間夜', dataIndex: 'unsold', key: 'unsold', align: 'right', width: 90, sorter: num((r) => r.unsold) },
      // 鄰日在手率：孤島型的低在手 (前後都滿) 與整段都弱，處方完全不同
      { title: '前一晚', dataIndex: 'prevOcc', key: 'prev', align: 'right', width: 90, render: pct,
        sorter: num((r) => r.prevOcc) },
      { title: '後一晚', dataIndex: 'nextOcc', key: 'next', align: 'right', width: 90, render: pct,
        sorter: num((r) => r.nextOcc) },
      // 團體/散客分層：看缺口落在哪一層。團體淨價為牌價 7 折，補團不比降價划算，所以不另開處方
      { title: '在手團體', dataIndex: 'otbGroup', key: 'grp', align: 'right', width: 100,
        sorter: num((r) => r.otbGroup),
        render: (v) => (v == null ? '—' : v) },
      { title: '在手散客', dataIndex: 'otbFit', key: 'fit', align: 'right', width: 100,
        sorter: num((r) => r.otbFit),
        render: (v) => (v == null ? '—' : v) },
      // 建議處方獨立一欄，不靠顏色或底色暗示 —— 這張表最後要回答的就是「這天到底該做什麼」
      // 與調漲表同為 390：內容雖短，兩張表每一欄同寬，抽屜也同寬，切換時不會跳動
      { title: '建議處方', key: 'rx', width: 390,
        render: () => <span style={{ color: MUTED_COLOR, whiteSpace: 'nowrap' }}>散客促銷 / 調降</span> },
      // 調漲表一直有這一欄，調降表卻沒有 —— 但這裡更需要它：下一欄的潛在房租就是
      // 待售間夜 × ADR，看得到結果卻看不到乘數；再下一欄的「降10%需填到」講的是降價
      // 一成，不知道現在多少錢就不知道那一成是多少。三欄相鄰，乘法關係才讀得出來。
      // 寬度與調漲表同為 100：同名欄不同寬的話，切換抽屜時該欄會左右跳動。
      { title: '目前 ADR', dataIndex: 'adr', key: 'adr', align: 'right', width: 100, sorter: num((r) => r.adr), render: money },
      { title: withHelp('潛在房租', COLUMN_HELP.lowerUpside), dataIndex: 'upside', key: 'upside', align: 'right', width: 120,
        showSorterTooltip: false, sorter: num((r) => r.upside), render: money },
      { title: '近30日淨進單', dataIndex: 'netPickup30', key: 'net', align: 'right', width: 130,
        sorter: num((r) => r.netPickup30), render: netPickupCell },
      // 130 而非 110：標題加了說明圖示
      { title: withHelp('降10%需填到', COLUMN_HELP.lowerBreakEven10), dataIndex: 'breakEven10', key: 'be',
        align: 'right', width: 130, showSorterTooltip: false, sorter: num((r) => r.breakEven10),
        render: (v) => `${v}%` },
    ]);
  }
  return base;
}

/**
 * 各表的總寬 (= 各欄 width 相加)，給 scroll.x 用。
 *
 * 指定明確寬度而不是交給自動分配：兩張表的欄位一一對應，寬度若各自由資料決定，
 * 切換 Drawer 時同一欄會左右跳動，掃讀對不齊。
 * 用 scroll.x 讓放不下時水平捲動，而不是壓縮欄寬硬塞 —— 數字欄斷行會讓人讀錯位數。
 *
 * 注意：不要加 tableLayout="fixed"。目前無 fixed 欄也無 ellipsis，rc-table 走 auto layout，
 * width 對瀏覽器是「至少這麼寬」，內容更長時仍會自己撐開；改成 fixed 會讓 width 變成硬上限，
 * 估窄了就開始斷行。
 */
export const TABLE_WIDTH = { raise: 1870, lower: 1870 };

/** antd Drawer 的 body 預設左右各 24px 內距。 */
export const DRAWER_BODY_PADDING = 48;

/**
 * 抽屜寬度。兩張表都很寬 (各 14 欄)。
 *
 * 原本寫死 1000px，調降表 1530px 於是被截斷：實測可視區只到「在手散客」，「建議處方」
 * 與四個金額欄全在畫面外 —— 這張表最後要回答的「這天該做什麼、值多少錢」預設都看不到。
 * 水平捲動在這裡不是解，因為要捲過去才知道有東西可看。
 *
 * 由 TABLE_WIDTH 推導而非各自寫死：加欄位時抽屜自動跟著長，不會再次失準。
 * 夾到 100vw 是慣例 (同 FeedbackItemsDrawer / OrdersDrawer)，否則窄螢幕上抽屜會比
 * 視窗寬，右半連關閉鈕都點不到。
 */
export const drawerWidthFor = (kind) =>
  `min(${TABLE_WIDTH[kind] + DRAWER_BODY_PADDING}px, 100vw)`;

/** 單張定價動作明細表。欄位依 kind 不同，前三欄共用。 */
export default function PricingActionsTable({ kind, rows, target, onPickDate }) {
  if (!rows || !rows.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={EMPTY_TEXT[kind]} />;
  }
  return (
    <Table rowKey="dat" size="small" pagination={false} scroll={{ x: TABLE_WIDTH[kind] }}
      dataSource={rows} columns={withKeyboardSort(columnsFor(kind, target, onPickDate))} />
  );
}
